import { AppView } from '../../types';

// ==========================================
// YAMOOPAY PRODUCT OPERATING SYSTEM TYPES
// ==========================================

export type StrategicRole = 'ACQUISITION' | 'CORE_VALUE' | 'RETENTION' | 'MONETIZATION' | 'PLATFORM' | 'STRATEGIC_OPTION';
export type PortfolioLifecycle = 'DISCOVERY' | 'INCUBATING' | 'GROWING' | 'CORE' | 'MATURE' | 'SUNSETTING' | 'RETIRED';
export type EvidenceType = 'INTERVIEW' | 'SUPPORT_CASE' | 'SURVEY' | 'ANALYTICS' | 'SEO_QUERY' | 'FEEDBACK' | 'EXPERIMENT';
export type ProblemStatus = 'OBSERVED' | 'VALIDATING' | 'VALIDATED' | 'DISPROVEN';
export type OpportunityStatus = 'IDENTIFIED' | 'RESEARCHING' | 'VALIDATED' | 'PRIORITIZED' | 'CONVERTED_TO_BET';
export type BetStatus = 'PROPOSED' | 'DISCOVERY' | 'APPROVED' | 'IN_PROGRESS' | 'MEASURING' | 'SUCCESS' | 'STOPPED';
export type FeatureLifecycle = 'CONCEPT' | 'DISCOVERY' | 'DEVELOPMENT' | 'ALPHA' | 'BETA' | 'GA' | 'MAINTENANCE' | 'SUNSET';
export type PRDStatus = 'DRAFT' | 'REVIEW' | 'APPROVED' | 'DELIVERY' | 'LAUNCHED';
export type ExperimentStatus = 'DRAFT' | 'RUNNING' | 'PAUSED' | 'DECIDED';
export type ExperimentDecisionType = 'SHIP' | 'ITERATE' | 'STOP' | 'INCONCLUSIVE';
export type OKRStatus = 'ACTIVE' | 'AT_RISK' | 'ACHIEVED' | 'MISSED';

export interface ProductStrategy {
  id: string;
  name: string;
  version: string;
  vision: string;
  strategicContext: string;
  targetSegments: string[];
  coreStrategicChoices: string[];
  nonGoals: string[]; // What YamooPay explicitly chooses NOT to do
  status: 'ACTIVE' | 'REVIEW' | 'DRAFT';
  updatedAt: string;
}

export interface ProductPortfolioItem {
  id: string;
  name: string;
  category: 'Currency' | 'Transfers' | 'Salary & Tax' | 'Loans' | 'Wealth' | 'Calculators' | 'AI Assistant' | 'Developer Platform' | 'My YamooPay';
  owner: string;
  strategicRole: StrategicRole;
  lifecycle: PortfolioLifecycle;
  targetSegments: string[];
  activeUsersMonthly: number;
  retentionD30Pct: number;
  revenueContributionRole: string;
  riskProfile: 'LOW' | 'MEDIUM' | 'HIGH';
  healthScore: number; // 0-100
}

export interface CustomerEvidence {
  id: string;
  type: EvidenceType;
  source: string;
  segment: string;
  market: string;
  observation: string;
  frequency: 'HIGH' | 'MEDIUM' | 'LOW';
  severity: 'CRITICAL' | 'MODERATE' | 'LOW';
  confidence: number; // 0-100
  dateRecorded: string;
}

export interface CustomerProblem {
  id: string;
  statement: string; // User-centered problem statement without embedded solution
  segment: string;
  market: string;
  frequency: 'DAILY' | 'WEEKLY' | 'OCCASIONAL';
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  currentWorkaround: string;
  evidenceIds: string[];
  status: ProblemStatus;
  owner: string;
}

export interface ProductOpportunity {
  id: string;
  problemId: string;
  title: string;
  strategicAlignmentScore: number; // 1-10
  userImpactScore: number; // 1-10
  businessImpactScore: number; // 1-10
  confidenceScore: number; // 1-10
  overallScore: number;
  status: OpportunityStatus;
  owner: string;
}

export interface ProductBet {
  id: string;
  opportunityId: string;
  title: string;
  hypothesis: string; // "If we do X for segment Y, we expect outcome Z because evidence W"
  targetOutcome: string;
  timeboxWeeks: number;
  investmentTier: 'SMALL' | 'MEDIUM' | 'LARGE';
  owner: string;
  successCriteria: string;
  killCriteria: string; // Define when to stop/sunset before starting
  status: BetStatus;
  confidencePct: number;
}

export interface ProductRequirementsDocument {
  id: string;
  betId: string;
  title: string;
  version: string;
  owner: string;
  problemStatement: string;
  goals: string[];
  nonGoals: string[];
  userJourneys: string[];
  functionalRequirements: string[];
  nonFunctionalRequirements: string[];
  analyticsEventsRequired: string[];
  securityPrivacyReviewPassed: boolean;
  status: PRDStatus;
  updatedAt: string;
}

export interface ProductFeature {
  id: string;
  name: string;
  portfolioItemId: string;
  prdId: string;
  owner: string;
  lifecycle: FeatureLifecycle;
  featureFlagKey: string;
  marketScope: string[];
  adoptionRatePct: number;
  timeToValueSeconds: number;
  flagCleanupDateDue: string;
  status: 'ACTIVE' | 'BETA' | 'CLEANUP_DUE' | 'DEPRECATED';
}

export interface ProductExperiment {
  id: string;
  featureId: string;
  title: string;
  hypothesis: string;
  primaryMetric: string;
  guardrailMetrics: string[];
  sampleSizeRequired: number;
  currentSampleSize: number;
  status: ExperimentStatus;
  decision?: ExperimentDecisionType;
  decisionRationale?: string;
  startedAt: string;
}

export interface ProductObjective {
  id: string;
  objective: string;
  owner: string;
  period: string; // e.g. "Q3 2026"
  keyResults: {
    statement: string;
    baseline: string;
    target: string;
    current: string;
    status: OKRStatus;
  }[];
  status: OKRStatus;
}

export interface ProductDecisionRecord {
  id: string;
  title: string;
  context: string;
  decision: string;
  evidenceSummary: string;
  alternativesConsidered: string[];
  decidedBy: string;
  decidedAt: string;
  reviewDate: string;
}

// ==========================================
// MOCK DATA STORES
// ==========================================

export const MOCK_STRATEGY: ProductStrategy = {
  id: 'strat-2026-global',
  name: 'YamooPay Global Financial Intelligence 2026 Strategy',
  version: 'v3.2',
  vision: 'Devenir la plateforme mondiale de référence pour l\'intelligence financière, les comparateurs de devises, la fiscalité internationale et le calcul déterministe.',
  strategicContext: 'Expansion multi-marchés (Europe, Afrique, Amérique du Nord, LatAm, Asie) centrée sur l\'acquisition organique gratuite, la confiance absolue et la modularité.',
  targetSegments: ['Salariés transfrontaliers', 'Expatriés & Diasporas', 'Apprenants financiers', 'Emprunteurs & Investisseurs', 'Développeurs API'],
  coreStrategicChoices: [
    'Utilité publique 100% gratuite avant tout mur d\'authentification',
    'Moteurs de calcul financiers déterministes et certifiés (pas de calculs génératifs LLM)',
    'Architecture Multi-Marchés et Multi-Devises native (Prompt 24 & 04)',
    'Agnosticisme IA total via AI Control Plane (Prompt 28)'
  ],
  nonGoals: [
    'PAS de trading de spéculation crypto/forex à haut risque',
    'PAS de dark patterns ni de faux sentiment d\'urgence',
    'PAS de monétisation forcée ou de vente d\'informations personnelles'
  ],
  status: 'ACTIVE',
  updatedAt: '2026-08-20'
};

export const MOCK_PORTFOLIO: ProductPortfolioItem[] = [
  {
    id: 'port-currency',
    name: 'Currency & Exchange Rate Intelligence',
    category: 'Currency',
    owner: 'Principal Currency PM',
    strategicRole: 'ACQUISITION',
    lifecycle: 'CORE',
    targetSegments: ['Expatriés & Diasporas', 'Voyageurs', 'Entreprises'],
    activeUsersMonthly: 1250000,
    retentionD30Pct: 44.5,
    revenueContributionRole: 'Organic User Acquisition Engine',
    riskProfile: 'LOW',
    healthScore: 96
  },
  {
    id: 'port-salary-tax',
    name: 'Salary, Payroll & Tax Intelligence',
    category: 'Salary & Tax',
    owner: 'Senior Tax PM',
    strategicRole: 'CORE_VALUE',
    lifecycle: 'CORE',
    targetSegments: ['Salariés transfrontaliers', 'RH & Payroll'],
    activeUsersMonthly: 840000,
    retentionD30Pct: 52.1,
    revenueContributionRole: 'High Engagement & Saved Calculation Retention',
    riskProfile: 'MEDIUM',
    healthScore: 94
  },
  {
    id: 'port-universal-calcs',
    name: 'Universal Deterministic Calculators',
    category: 'Calculators',
    owner: 'Lead Calculator PM',
    strategicRole: 'CORE_VALUE',
    lifecycle: 'CORE',
    targetSegments: ['Tous publics'],
    activeUsersMonthly: 2100000,
    retentionD30Pct: 61.2,
    revenueContributionRole: 'Core Organic Utility',
    riskProfile: 'LOW',
    healthScore: 98
  },
  {
    id: 'port-ai-assistant',
    name: 'AI Financial Assistant & Grounded RAG',
    category: 'AI Assistant',
    owner: 'Senior AI PM',
    strategicRole: 'STRATEGIC_OPTION',
    lifecycle: 'GROWING',
    targetSegments: ['Financial Learners', 'Expatriés'],
    activeUsersMonthly: 410000,
    retentionD30Pct: 38.0,
    revenueContributionRole: 'Engagement Booster & Conversational Discovery',
    riskProfile: 'HIGH',
    healthScore: 89
  }
];

export const MOCK_EVIDENCES: CustomerEvidence[] = [
  {
    id: 'ev-01',
    type: 'INTERVIEW',
    source: 'Étude Utilisateurs Diasporas EU-Afrique (30 interviews)',
    segment: 'Expatriés & Diasporas',
    market: 'France / Sénégal / Côte d\'Ivoire',
    observation: 'Les utilisateurs perdent jusqu\'à 4.5% en frais cachés lors du choix de leur prestataire de transfert par manque de transparence des taux effectifs.',
    frequency: 'HIGH',
    severity: 'CRITICAL',
    confidence: 94,
    dateRecorded: '2026-08-10'
  },
  {
    id: 'ev-02',
    type: 'ANALYTICS',
    source: 'Product Analytics Query Insights (Prompt 11)',
    segment: 'Salariés transfrontaliers',
    market: 'Suisse / France',
    observation: '78% des utilisateurs qui simulent leur salaire frontalier souhaitent comparer instantanément le reste à vivre après cotisations et impôt à la source.',
    frequency: 'HIGH',
    severity: 'MODERATE',
    confidence: 98,
    dateRecorded: '2026-08-15'
  }
];

export const MOCK_PROBLEMS: CustomerProblem[] = [
  {
    id: 'prob-hidden-fx-costs',
    statement: 'Les utilisateurs de transferts transfrontaliers ont des difficultés à comparer le coût réel global (frais d\'envoi + majoration de taux de change).',
    segment: 'Expatriés & Diasporas',
    market: 'Global',
    frequency: 'WEEKLY',
    severity: 'HIGH',
    currentWorkaround: 'Calcul manuel fastidieux sur tableur Excel avec plusieurs onglets ouverts.',
    evidenceIds: ['ev-01'],
    status: 'VALIDATED',
    owner: 'Principal Discovery Lead'
  },
  {
    id: 'prob-cross-border-tax-complexity',
    statement: 'Les travailleurs transfrontaliers manquent de lisibilité sur le revenu net réel après application des conventions fiscales d\'évitement de double imposition.',
    segment: 'Salariés transfrontaliers',
    market: 'Europe (CH/FR, BE/FR, DE/FR)',
    frequency: 'DAILY',
    severity: 'HIGH',
    currentWorkaround: 'Consultation de forums spécialisés non certifiés et estimations imprécises.',
    evidenceIds: ['ev-02'],
    status: 'VALIDATED',
    owner: 'Senior Tax PM'
  }
];

export const MOCK_OPPORTUNITIES: ProductOpportunity[] = [
  {
    id: 'opp-real-fx-cost-calculator',
    problemId: 'prob-hidden-fx-costs',
    title: 'Comparateur de Frais Effectifs de Transfert Transparent en Direct',
    strategicAlignmentScore: 10,
    userImpactScore: 9,
    businessImpactScore: 8,
    confidenceScore: 9,
    overallScore: 9.1,
    status: 'PRIORITIZED',
    owner: 'Senior Transfer PM'
  },
  {
    id: 'opp-net-net-tax-simulator',
    problemId: 'prob-cross-border-tax-complexity',
    title: 'Simulateur Dual-Tax Net-Net avec Déduction Conventionnelle Instantanée',
    strategicAlignmentScore: 9,
    userImpactScore: 9,
    businessImpactScore: 8,
    confidenceScore: 9,
    overallScore: 8.8,
    status: 'CONVERTED_TO_BET',
    owner: 'Senior Tax PM'
  }
];

export const MOCK_BETS: ProductBet[] = [
  {
    id: 'bet-dual-tax-calculator',
    opportunityId: 'opp-net-net-tax-simulator',
    title: 'Pari Produit : Calculateur Bi-National de Salaire Net Reste à Vivre',
    hypothesis: 'Si nous offrons une comparaison immédiate Net-Net entre pays de résidence et d\'emploi pour le segment des transfrontaliers, le taux de rétention à 30 jours augmentera de 15 points.',
    targetOutcome: 'Rétention D30 passant de 52% à 67% sur la cohorte transfrontalière.',
    timeboxWeeks: 6,
    investmentTier: 'MEDIUM',
    owner: 'Senior Tax PM',
    successCriteria: 'Augmentation de +15% des simulations sauvegardées et partagées.',
    killCriteria: 'Si l\'adoption D14 reste sous les 5% ou si le temps moyen d\'exécution dépasse 2.5 secondes.',
    status: 'IN_PROGRESS',
    confidencePct: 88
  }
];

export const MOCK_PRDS: ProductRequirementsDocument[] = [
  {
    id: 'prd-dual-tax-v1',
    betId: 'bet-dual-tax-calculator',
    title: 'PRD — Simulateur Salaire Dual-Tax & Convention Fiscale Transfrontalière',
    version: 'v1.4',
    owner: 'Senior Tax PM',
    problemStatement: 'Les utilisateurs transfrontaliers perdent du temps à calculer manuellement l\'impact net après cotisations dans un pays et impôt dans l\'autre.',
    goals: [
      'Fournir un résultat déterministe certifié en < 300ms',
      'Afficher la décomposition explicite des tranches d\'imposition',
      'Permettre la sauvegarde anonyme sans création de compte obligatoire'
    ],
    nonGoals: [
      'Ne pas remplir automatiquement les déclarations d\'impôt officielles',
      'Ne pas émettre de conseil fiscal personnalisé sous licence réglementée'
    ],
    userJourneys: [
      'Choix pays résidence et pays d\'emploi -> Saisie salaire brut -> Affichage instantané du net-net'
    ],
    functionalRequirements: [
      'Intégration moteur déterministe Prompt 06',
      'Export PDF/JSON certifié',
      'Bouton de partage sécurisé de la simulation'
    ],
    nonFunctionalRequirements: [
      'P95 Latence < 300ms',
      'Conformité RGPD Zero-Storage sans consentement'
    ],
    analyticsEventsRequired: ['calc_dual_tax_executed', 'calc_dual_tax_saved', 'calc_dual_tax_shared'],
    securityPrivacyReviewPassed: true,
    status: 'APPROVED',
    updatedAt: '2026-08-21'
  }
];

export const MOCK_FEATURES: ProductFeature[] = [
  {
    id: 'feat-dual-tax-calc',
    name: 'Moteur de Calcul Dual-Tax Frontalier',
    portfolioItemId: 'port-salary-tax',
    prdId: 'prd-dual-tax-v1',
    owner: 'Senior Tax PM',
    lifecycle: 'BETA',
    featureFlagKey: 'ff_dual_tax_calculator_v1',
    marketScope: ['FR', 'CH', 'BE', 'DE', 'LU'],
    adoptionRatePct: 41.2,
    timeToValueSeconds: 4.5,
    flagCleanupDateDue: '2026-10-15',
    status: 'BETA'
  }
];

export const MOCK_EXPERIMENTS: ProductExperiment[] = [
  {
    id: 'exp-tax-onboarding-flow',
    featureId: 'feat-dual-tax-calc',
    title: 'Expérience A/B — Saisie Progressive vs Formulaire Unique Dual-Tax',
    hypothesis: 'La saisie progressive étape par étape réduira l\'abandon du formulaire de 22%.',
    primaryMetric: 'form_completion_rate',
    guardrailMetrics: ['calc_error_rate', 'support_ticket_rate'],
    sampleSizeRequired: 15000,
    currentSampleSize: 11200,
    status: 'RUNNING',
    startedAt: '2026-08-18'
  }
];

export const MOCK_OKRS: ProductObjective[] = [
  {
    id: 'okr-q3-utility',
    objective: 'Renforcer le statut de YamooPay comme utilité financière mondiale incontournable et transparente.',
    owner: 'Chief Product Officer',
    period: 'Q3 2026',
    keyResults: [
      {
        statement: 'Augmenter le taux d\'accomplissement de premier calcul de 72% à 88%.',
        baseline: '72%',
        target: '88%',
        current: '84%',
        status: 'ACTIVE'
      },
      {
        statement: 'Maintenir un Time-to-Value médian sous les 5.0 secondes sur tous les comparateurs.',
        baseline: '6.2s',
        target: '5.0s',
        current: '4.5s',
        status: 'ACHIEVED'
      }
    ],
    status: 'ACTIVE'
  }
];

export const MOCK_DECISIONS: ProductDecisionRecord[] = [
  {
    id: 'pdr-001',
    title: 'Choix Stratégique : Reste à Vivre 100% Gratuit Sans Inscription Obligatoire',
    context: 'Débat interne sur la possibilité de demander une création de compte préalable avant d\'afficher le résultat financier complet.',
    decision: 'Accès 100% immédiat et gratuit aux résultats. La création de compte My YamooPay est uniquement proposée pour la sauvegarde et les alertes.',
    evidenceSummary: 'Les études utilisateurs démontrent que 68% des abandons surviennent lors d\'une obligation de création de compte prématurée.',
    alternativesConsidered: ['Freemium partiel masquant les impôts', 'Inscription sociale obligatoire'],
    decidedBy: 'Chief Product Officer & VP Product',
    decidedAt: '2026-08-01',
    reviewDate: '2026-11-01'
  }
];

// ==========================================
// STORE CLASS
// ==========================================

class ProductOSStore {
  private strategy: ProductStrategy = { ...MOCK_STRATEGY };
  private portfolio: ProductPortfolioItem[] = [...MOCK_PORTFOLIO];
  private evidences: CustomerEvidence[] = [...MOCK_EVIDENCES];
  private problems: CustomerProblem[] = [...MOCK_PROBLEMS];
  private opportunities: ProductOpportunity[] = [...MOCK_OPPORTUNITIES];
  private bets: ProductBet[] = [...MOCK_BETS];
  private prds: ProductRequirementsDocument[] = [...MOCK_PRDS];
  private features: ProductFeature[] = [...MOCK_FEATURES];
  private experiments: ProductExperiment[] = [...MOCK_EXPERIMENTS];
  private okrs: ProductObjective[] = [...MOCK_OKRS];
  private decisions: ProductDecisionRecord[] = [...MOCK_DECISIONS];

  // Getters
  getStrategy() { return this.strategy; }
  getPortfolio() { return this.portfolio; }
  getEvidences() { return this.evidences; }
  getProblems() { return this.problems; }
  getOpportunities() { return this.opportunities; }
  getBets() { return this.bets; }
  getPRDs() { return this.prds; }
  getFeatures() { return this.features; }
  getExperiments() { return this.experiments; }
  getOKRs() { return this.okrs; }
  getDecisions() { return this.decisions; }

  // Actions
  addBet(newBet: Omit<ProductBet, 'id'>) {
    const created: ProductBet = {
      ...newBet,
      id: `bet-${Date.now()}`
    };
    this.bets.unshift(created);
    return created;
  }

  addPRD(newPRD: Omit<ProductRequirementsDocument, 'id' | 'updatedAt'>) {
    const created: ProductRequirementsDocument = {
      ...newPRD,
      id: `prd-${Date.now()}`,
      updatedAt: new Date().toISOString().split('T')[0]
    };
    this.prds.unshift(created);
    return created;
  }
}

export const productOSStore = new ProductOSStore();
